import React, { useEffect, useMemo, useRef, useState } from 'react';
import axiosConn from '../../axiosConn';

/**
 * PushSubscribeButton
 * Props:
 *  - jwt: string (Bearer token)
 *  - vapidKey: string (public VAPID key, base64 URL-safe)
 *  - className?: string
 *  - product?: 'trailblazer' | 'connect' | 'legacy' (copy tweaks)
 *  - onStateChange?: (state) => void  // { supported, permission, subscribed, ready }
 *  - onSubscribed?: () => void
 */
export default function PushSubscribeButton({
  jwt,
  vapidKey,
  className = '',
  copy = {},
  product,
  onStateChange,
  onSubscribed,
  variant = 'default',
  buttonRef = null,
}) {
  const [supported, setSupported] = useState(true);
  const [ready, setReady] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default',
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  // 🔊 prime only once
  const audioPrimedRef = useRef(false);
  const clickLockRef = useRef(false);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const isIOS = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent || '';
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  }, []);

  const isIOSInstalled = useMemo(() => {
    if (!isIOS) return false;
    // iOS Safari push requires PWA installed (iOS 16.4+)
    return window.navigator.standalone === true;
  }, [isIOS]);

  const resolvedCopy = useMemo(() => {
    const base =
      product === 'trailblazer'
        ? {
            blockedLabel: 'Notifications blocked',
            compactEnabledLabel: 'Hunt alerts on',
            compactEnableLabel: 'Enable hunt alerts',
            disableLabel: 'Disable Push',
            enableLabel: 'Enable Push Notifications',
            helpText: 'Get instant hunt updates, check-ins, and rewards.',
            iosInstallText:
              'On iPhone/iPad, install to Home Screen to enable push.',
            subscribedText:
              'You will receive alerts even when the app is closed.',
            successMessage: 'Push enabled. You will get real-time updates.',
            unsupportedLabel: 'Not supported',
            unsupportedText: 'Your browser does not support web push.',
          }
        : product === 'localops'
        ? {
            blockedLabel: 'Notifications blocked',
            compactEnabledLabel: 'LocalOps alerts on',
            compactEnableLabel: 'Enable LocalOps alerts',
            disableLabel: 'Turn off alerts',
            enableLabel: 'Enable alerts',
            helpText:
              'Get notified when nearby tasks, rides, and local offers are posted.',
            iosInstallText:
              'On iPhone/iPad, open from your Home Screen to enable alerts.',
            subscribedText: 'LocalOps alerts are on.',
            successMessage: 'LocalOps alerts are on.',
            unsupportedLabel: 'Not supported',
            unsupportedText: 'This browser does not support LocalOps alerts.',
          }
        : {
            blockedLabel: 'Notifications blocked',
            compactEnabledLabel: 'Alerts on',
            compactEnableLabel: 'Enable alerts',
            disableLabel: 'Turn off alerts',
            enableLabel: 'Enable alerts',
            helpText:
              'Get notified about messages, requests, LocalOps tasks, and important updates.',
            iosInstallText:
              'On iPhone/iPad, open from your Home Screen to enable alerts.',
            subscribedText: 'Alerts are on.',
            successMessage: 'Alerts are on.',
            unsupportedLabel: 'Not supported',
            unsupportedText: 'Your browser does not support JG alerts.',
          };
    return { ...base, ...copy };
  }, [copy, product]);

  // --- utils ---
  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const raw = atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
    return out;
  }

  async function getSWRegistrationSafe(timeoutMs = 8000) {
    // Race SW.ready with a timeout + getRegistration fallback to avoid hanging
    if (!('serviceWorker' in navigator)) return null;
    const timeout = new Promise((res) =>
      setTimeout(() => res(null), timeoutMs),
    );
    const readyP = (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        return reg || (await navigator.serviceWorker.getRegistration());
      } catch {
        return navigator.serviceWorker.getRegistration();
      }
    })();
    return (await Promise.race([readyP, timeout])) || null;
  }

  function primeAudioOnce() {
    if (audioPrimedRef.current) return;
    try {
      const a = new Audio('/sounds/notify.mp3'); // ensure this exists
      a.volume = 0;
      a.play()
        .then(() => {
          a.pause();
          a.currentTime = 0;
          audioPrimedRef.current = true;
        })
        .catch(() => {});
    } catch {}
  }

  async function syncSubscriptionToServer(reg, token) {
    try {
      const sub = await reg.pushManager.getSubscription();
      if (!sub) return false;
      // Many servers treat this POST as upsert (idempotent)
      await axiosConn.post('notifications/register-webpush', sub, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      return true;
    } catch {
      return false;
    }
  }

  function emitState(next = {}) {
    if (typeof onStateChange === 'function') {
      onStateChange({
        supported,
        permission,
        subscribed,
        ready,
        ...next,
      });
    }
  }

  // --- initial check + silent sync on mount ---
  useEffect(() => {
    let mounted = true;
    (async () => {
      const ok =
        typeof window !== 'undefined' &&
        window.isSecureContext &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        typeof Notification !== 'undefined';

      if (mounted) setSupported(ok);
      if (!ok) {
        emitState({ supported: false });
        return;
      }

      if (mounted) setPermission(Notification.permission);

      const reg = await getSWRegistrationSafe();
      if (!reg) {
        if (mounted) setReady(false);
        emitState({ ready: false });
        return;
      }

      if (mounted) setReady(true);
      const sub = await reg.pushManager.getSubscription();
      const hasSub = !!sub;
      if (mounted) setSubscribed(hasSub);

      // Silent upsert in case server lost it (data cleared/pruned)
      if (hasSub && jwt) {
        await syncSubscriptionToServer(reg, jwt);
      }

      emitState({ ready: true, subscribed: hasSub });
    })();

    // Re-eval when tab regains focus (helps after login/redirect)
    const onVis = async () => {
      if (document.visibilityState !== 'visible') return;
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        if (mounted) setReady(true);
        const sub = await reg.pushManager.getSubscription();
        if (mounted) setSubscribed(!!sub);
        emitState({ ready: true, subscribed: !!sub });
      }
      if (mounted)
        setPermission(
          typeof Notification !== 'undefined'
            ? Notification.permission
            : 'default',
        );
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      mounted = false;
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwt]);

  // --- actions ---
  async function enablePush() {
    if (clickLockRef.current) return;
    clickLockRef.current = true;
    setMsg('');
    primeAudioOnce();

    try {
      if (!jwt) {
        setMsg('Please sign in to enable notifications.');
        return;
      }
      if (!supported) {
        setMsg(resolvedCopy.unsupportedText);
        return;
      }
      if (isIOS && !isIOSInstalled) {
        setMsg(resolvedCopy.iosInstallText);
        return;
      }
      if (!vapidKey) {
        setMsg('Missing VAPID key on client.');
        return;
      }
      const vapidKeyBytes = urlBase64ToUint8Array(vapidKey);
      if (vapidKeyBytes.length !== 65 || vapidKeyBytes[0] !== 4) {
        setMsg('Invalid VAPID public key.');
        return;
      }

      setLoading(true);

      // Request permission (only prompts if "default")
      let perm =
        typeof Notification !== 'undefined'
          ? Notification.permission
          : 'denied';
      if (perm === 'default') {
        perm = await Notification.requestPermission();
      }
      setPermission(perm);
      if (perm !== 'granted') {
        setMsg(
          perm === 'denied'
            ? 'Notifications are blocked. Allow them in your browser site settings.'
            : 'Permission was not granted.',
        );
        return;
      }

      // Ensure SW ready
      const reg = await getSWRegistrationSafe();
      if (!reg) {
        setMsg('Service worker not ready. Reload and try again.');
        return;
      }

      // Ensure subscription (resubscribe if missing) with diagnostics + retry
      const subscribeOnce = async () => {
        try {
          return await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: vapidKeyBytes,
          });
        } catch (err) {
          console.warn('[PushSubscribeButton] subscribe error', {
            name: err?.name,
            code: err?.code,
            message: err?.message,
            permission:
              typeof Notification !== 'undefined'
                ? Notification.permission
                : 'unknown',
            isSecureContext: window?.isSecureContext,
          });
          throw err;
        }
      };

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        try {
          sub = await subscribeOnce();
        } catch (err) {
          try {
            const existing = await reg.pushManager.getSubscription();
            if (existing) await existing.unsubscribe();
            await reg.update?.();
          } catch {}
          try {
            sub = await subscribeOnce();
          } catch (err2) {
            const hint =
              'Browser could not reach the push service (often blocked by network or extensions).';
            throw new Error(
              err2?.message ? `${err2.message} — ${hint}` : hint,
            );
          }
        }
      }

      // Send to backend (idempotent upsert)
      const resp = await axiosConn.post(
        'notifications/register-webpush',
        sub,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
          },
        },
      );

      if (!(resp?.status === 200 || resp?.status === 201)) {
        // Roll back local sub if server refused
        try {
          await sub.unsubscribe();
        } catch {}
        throw new Error(
          `Server rejected subscription (${resp?.status || 'unknown'})`,
        );
      }

      if (!isMountedRef.current) return;
      setSubscribed(true);
      setMsg(resolvedCopy.successMessage);
      emitState({ subscribed: true, permission: 'granted' });
      if (typeof onSubscribed === 'function') {
        onSubscribed();
      }
    } catch (err) {
      console.warn('[PushSubscribeButton] enable error:', err?.message || err);
      if (isMountedRef.current) {
        setMsg(err?.message || 'Failed to enable push.');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
      clickLockRef.current = false;
    }
  }

  async function disablePush() {
    if (clickLockRef.current) return;
    clickLockRef.current = true;
    setMsg('');

    try {
      if (!supported) return;
      setLoading(true);

      const reg = await getSWRegistrationSafe();
      if (!reg) {
        if (isMountedRef.current) setMsg('Service worker not ready.');
        return;
      }
      const sub = await reg.pushManager.getSubscription();
      if (!sub) {
        if (isMountedRef.current) {
          setSubscribed(false);
          setMsg('Push disabled.');
          emitState({ subscribed: false });
        }
        return;
      }

      // Best-effort server prune (if your backend supports DELETE)
      try {
        await axiosConn.delete('notifications/register-webpush', {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
          },
          data: { endpoint: sub.endpoint },
        });
      } catch {
        /* ignore */
      }

      await sub.unsubscribe();

      if (isMountedRef.current) {
        setSubscribed(false);
        setMsg('Push disabled.');
        emitState({ subscribed: false });
      }
    } catch (err) {
      console.warn('[PushSubscribeButton] disable error:', err?.message || err);
      if (isMountedRef.current) setMsg('Failed to disable push.');
    } finally {
      if (isMountedRef.current) setLoading(false);
      clickLockRef.current = false;
    }
  }

  function actionLabel() {
    if (!supported) return resolvedCopy.unsupportedLabel;
    if (permission === 'denied') return resolvedCopy.blockedLabel;
    return subscribed ? resolvedCopy.disableLabel : resolvedCopy.enableLabel;
  }

  const helpText = (() => {
    if (!supported) return resolvedCopy.unsupportedText;
    if (permission === 'denied') {
      return 'Notifications are blocked. Allow them in your browser or phone settings.';
    }
    if (isIOS && !isIOSInstalled)
      return resolvedCopy.iosInstallText;
    if (!subscribed) {
      return resolvedCopy.helpText;
    }
    return resolvedCopy.subscribedText;
  })();

  const disabled =
    loading || !supported || permission === 'denied' || (!ready && !subscribed);

  if (variant === 'compact') {
    const compactLabel = subscribed
      ? resolvedCopy.compactEnabledLabel
      : resolvedCopy.compactEnableLabel;
    return (
      <button
        type="button"
        ref={buttonRef}
        onClick={subscribed ? disablePush : enablePush}
        disabled={disabled}
        aria-disabled={disabled}
        aria-live="polite"
        title={helpText}
        className={className}
        style={{
          padding: '6px 10px',
          borderRadius: 999,
          fontWeight: 700,
          fontSize: 12,
          border: '1px solid rgba(148,163,184,0.4)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: subscribed ? '#e2e8f0' : '#facc15',
          color: subscribed ? '#0f172a' : '#1f2937',
        }}
      >
        {loading ? '...' : compactLabel}
      </button>
    );
  }

  return (
    <div
      className={className}
      style={{ display: 'grid', gap: 8, maxWidth: 420 }}
    >
      <button
        type="button"
        onClick={subscribed ? disablePush : enablePush}
        disabled={disabled}
        aria-disabled={disabled}
        aria-live="polite"
        style={{
          padding: '10px 14px',
          borderRadius: 10,
          fontWeight: 700,
          border: '1px solid #ddd',
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: subscribed ? '#f3f4f6' : '#facc15',
        }}
        title={
          permission === 'denied'
            ? 'Allow notifications in your browser site settings'
            : ''
        }
      >
        {loading ? 'Please wait…' : actionLabel()}
      </button>

      <div style={{ fontSize: 13, color: '#555' }}>{helpText}</div>
      {msg && <div style={{ fontSize: 13, color: '#8a2be2' }}>{msg}</div>}
    </div>
  );
}
